export default {
  initCapacities({ commit }, { minSocFeedInOpt, minSocSelfConsumptionOpt, minSocPeakShavingOpt }) {
    updateState(commit, { minSocFeedInOpt, minSocSelfConsumptionOpt, minSocPeakShavingOpt })
  },

  updateFeedInCapacity({ state, getters, commit }, value) {
    const capacity = Math.round(Math.min(parseInt(value) || 0, state.maxSoc));
    const supportsPeakShavingOpt = getters.supportsOperatingMode('PEAK_SHAVING_OPT');

    const minSocFeedInOpt = Math.max(state.maxSoc - capacity, state.minSoc);
    const minSocSelfConsumptionOpt = Math.min(state.minSocSelfConsumptionOpt, minSocFeedInOpt);
    const minSocPeakShavingOpt = supportsPeakShavingOpt ? Math.min(state.minSocPeakShavingOpt, minSocSelfConsumptionOpt) : null;
    updateState(commit, { minSocFeedInOpt, minSocSelfConsumptionOpt, minSocPeakShavingOpt })
  },

  updateSelfConsumptionCapacity({ state, getters,commit }, value) {
    const capacity = calculateCapacity(value, getters.configurableCapacity)
    const supportsFeedInOpt = getters.supportsOperatingMode('FEED_IN_OPT')
    let minSocFeedInOpt = supportsFeedInOpt ? state.minSocFeedInOpt : null

    // Special handling for self-consumption optimization
    if (capacity > getters.selfConsumptionOptCapacity) {
      minSocFeedInOpt = supportsFeedInOpt ? Math.min(state.minSocSelfConsumptionOpt + capacity, state.maxSoc) : null
    }
    const feedInOptCapacity = state.maxSoc - (minSocFeedInOpt || state.maxSoc)
    const minSocSelfConsumptionOpt = (minSocFeedInOpt || state.maxSoc) - capacity
    let minSocPeakShavingOpt = null
    if (getters.operatingModeEnabled('PEAK_SHAVING_OPT')) {
      const peakShavingOptCapacity = Math.max(0, getters.configurableCapacity - feedInOptCapacity - capacity - getters.backupCapacity)
      minSocPeakShavingOpt = Math.max(state.minSoc, minSocSelfConsumptionOpt - peakShavingOptCapacity)
    } else if (getters.supportsOperatingMode('PEAK_SHAVING_OPT')) {
      minSocPeakShavingOpt = Math.min(minSocSelfConsumptionOpt, state.minSocPeakShavingOpt)
    }
    updateState(commit, { minSocFeedInOpt, minSocSelfConsumptionOpt, minSocPeakShavingOpt })
  },

  updatePeakShavingCapacity({ state, getters, commit }, value) {
    const socValues = calculateSocValues({
      value,
      state,
      getters,
      maxCapacity: getters.configurableCapacity,
      mode: 'PEAK_SHAVING_OPT'
    })
    updateState(commit, socValues)
  },

  updateBackupCapacity({ state, getters, commit }, value) {
    const socValues = calculateSocValues({
      value,
      state,
      getters,
      maxCapacity: state.minSoc + getters.totalAvailableBackupCapacity,
      mode: 'BACKUP_ENERGY'
    })
    updateState(commit, socValues)
  },

  updateSystemReserveCapacity({ state, getters, commit }, value) {
    const socValues = calculateSocValues({
      value,
      state,
      getters,
      maxCapacity: state.maxSoc,
      mode: 'SYSTEM_RESERVE'
    })
    updateState(commit, socValues)
  },
}

function calculateCapacity(value, maxCapacity) {
  return Math.round(Math.min(parseInt(value) || 0, maxCapacity))
}

function calculateSocValues({ value, state, getters, maxCapacity, mode }) {
  const capacity = calculateCapacity(value, maxCapacity);
  const supportsFeedInOpt = getters.supportsOperatingMode('FEED_IN_OPT');
  const supportsPeakShavingOpt = getters.supportsOperatingMode('PEAK_SHAVING_OPT');
  const enabledPeakShavingOpt = getters.operatingModeEnabled('PEAK_SHAVING_OPT');

  const referenceCapacity = getReferenceCapacity(mode, getters);
  const minSocReference = mode === 'SYSTEM_RESERVE' ? state.depthOfDischargeCapacity : state.minSoc;
  const delta = capacity - referenceCapacity;
  const maxSoc = mode == 'BACKUP_ENERGY' && !supportsPeakShavingOpt ? state.minSoc + getters.totalAvailableBackupCapacity : state.maxSoc

  const minSocSelfConsumptionOpt = calculateSelfConsumptionOpt(
    delta,
    minSocReference,
    state.minSocSelfConsumptionOpt,
    maxSoc 
  );

  const minSocFeedInOpt = supportsFeedInOpt ? calculateFeedInOpt(delta, state, minSocSelfConsumptionOpt) : null

  const maxSocPeakShavingOpt = mode == 'BACKUP_ENERGY' ? maxCapacity : minSocSelfConsumptionOpt
  const params = { mode, delta, state, maxSocPeakShavingOpt, capacity, enabledPeakShavingOpt, minSocReference }
  const minSocPeakShavingOpt = calculatePeakShavingOpt(params)
  const minSoc = mode == 'SYSTEM_RESERVE' ? calculateSystemReserve(delta, minSocReference, state) : null

  return { minSocFeedInOpt, minSocSelfConsumptionOpt, minSocPeakShavingOpt, minSoc };
}

function getReferenceCapacity(mode, getters) {
  const capacityMap = {
    'PEAK_SHAVING_OPT': getters.peakShavingOptCapacity,
    'BACKUP_ENERGY': getters.backupCapacity,
    'SYSTEM_RESERVE': getters.systemReserveCapacity
  };
  return capacityMap[mode];
}

function calculateSelfConsumptionOpt(delta, minSocReference, currentValue, maxSoc) {
  return delta < 0
    ? Math.max(minSocReference, currentValue + delta)
    : Math.min(currentValue + delta, maxSoc);
}

function calculateFeedInOpt(delta, state, minSocSelfConsumptionOpt) {
  if (delta <= 0) return state.minSocFeedInOpt
  return Math.max(state.minSocFeedInOpt, minSocSelfConsumptionOpt)
}

function calculatePeakShavingOpt(params) {
  const {
    mode,
    delta,
    state,
    maxSocPeakShavingOpt,
    capacity,
    enabledPeakShavingOpt,
    minSocReference
  } = params;

  if (mode === 'PEAK_SHAVING_OPT') {
    return delta < 0
      ? Math.min(state.minSocPeakShavingOpt, maxSocPeakShavingOpt)
      : Math.max(state.minSoc, maxSocPeakShavingOpt - capacity);
  } else if (enabledPeakShavingOpt) {
    return delta < 0
      ? Math.max(minSocReference, state.minSocPeakShavingOpt + delta)
      : Math.min(state.minSocPeakShavingOpt + delta, maxSocPeakShavingOpt);
  } else {
    return Math.min(state.minSocPeakShavingOpt + delta, maxSocPeakShavingOpt)
  }
}

function calculateSystemReserve(delta, minSocReference, state) {
  return delta < 0
    ? Math.max(minSocReference, state.minSoc + delta)
    : Math.min(state.minSoc + delta, state.maxSoc);
}

function updateState(commit, values) {
  const filteredSocValues = Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== null)
  );
  commit('updateMinSocValues', filteredSocValues);
}
